import { useLazyGetPermitToDigsByPermitRegisterId } from "../operations/queries/getPermitToDigsByPermitRegisterId";
import { useGetPermitToDigFilterOptions } from "../operations/queries/getPermitToDigFilterOptions";
import PermitsList from "../components/PermitsList";
import { usePermitRegisterPolicies } from "../hooks/usePermitRegisterPolicies";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { StateKey } from "../models/globalTypes";
import { useQueryString } from "../hooks/useQueryString";
import { StateKeyList } from "../models/StateKey";
import { EsriIntegrationTypes } from "../models/EsriIntegrationType";
import { useOnProjectPermitToDigDetailsChanged } from "../operations/subscriptions/onProjectPermitToDigDetailsChanged";
import { useAppContext } from "../hooks/useAppContext";

const PermitsListContainer = () => {
  const { currentOrganization, currentProject, currentPermitRegister } = useAppContext();

  const history = useHistory();
  const queryString = useQueryString();

  const searchString = queryString.get("search") ?? "";
  const stateFilterKeyString = queryString.get("state");
  const assignedToFilter = queryString.get("assignedToId") ?? "";
  const createdByFilter = queryString.get("createdById") ?? "";
  const asBuiltRequiredFilter =
    queryString.get("asBuiltRequired") === "true"
      ? true
      : queryString.get("asBuiltRequired") === "false"
        ? false
        : null;
  const permitTypeFilter = queryString.get("permitTypeId") ?? "";
  const stateFilter: StateKey | null = stateFilterKeyString
    ? StateKeyList[stateFilterKeyString as keyof typeof StateKeyList].key
    : null;

  const { authorizedToAddPermit, authorizedToRequestPermit } = usePermitRegisterPolicies();
  const [paginationInfo, setPaginationInfo] = useState({ pageSize: 0, pageIndex: 0 });

  const {
    getPermitToDigsByPermitRegisterId: getPermits,
    status: getPermitsStatus,
    data: permits,
    refetch,
  } = useLazyGetPermitToDigsByPermitRegisterId();

  const { data } = useOnProjectPermitToDigDetailsChanged(currentProject?.id);

  const { data: filterOptionsData } = useGetPermitToDigFilterOptions(currentPermitRegister?.id);

  /* useEffect(() => {
    if (permitListChanged && refetch) {
      refetch();
    }
  }, [permitListChanged, refetch]); */

  const handlePageInfoChange = useCallback((pageIndex: number, pageSize: number) => {
    if (pageSize !== paginationInfo.pageSize || pageIndex !== paginationInfo.pageIndex) {
      setPaginationInfo({ pageSize, pageIndex });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchPermits = (searchString: string) => {
    if (searchString) {
      queryString.set("search", searchString);
    } else {
      queryString.delete("search");
    }

    setPaginationInfo({ ...paginationInfo, pageIndex: 0 });

    history.push({
      search: queryString.toString(),
    });
  };

  const handleStateFilter = (stateKey: StateKey) => {
    if (stateKey) {
      queryString.set("state", stateKey);
    } else {
      queryString.delete("state");
    }

    setPaginationInfo({ ...paginationInfo, pageIndex: 0 });

    history.push({
      search: queryString.toString(),
    });
  };

  const handleAssignedToFilter = (assignedToId: string) => {
    if (assignedToId) {
      queryString.set("assignedToId", assignedToId);
    } else {
      queryString.delete("assignedToId");
    }

    setPaginationInfo({ ...paginationInfo, pageIndex: 0 });

    history.push({
      search: queryString.toString(),
    });
  };

  const handleCreatedByFilter = (createdById: string) => {
    if (createdById) {
      queryString.set("createdById", createdById);
    } else {
      queryString.delete("createdById");
    }

    setPaginationInfo({ ...paginationInfo, pageIndex: 0 });

    history.push({
      search: queryString.toString(),
    });
  };

  const handleAsBuiltRequiredFilterChange = (asBuiltRequired: boolean | null) => {
    if (asBuiltRequired !== null) {
      queryString.set("asBuiltRequired", asBuiltRequired.toString());
    } else {
      queryString.delete("asBuiltRequired");
    }

    setPaginationInfo({ ...paginationInfo, pageIndex: 0 });

    history.push({
      search: queryString.toString(),
    });
  };

  const handlePermitTypeFilter = (permitTypeId: string) => {
    if (permitTypeId) {
      queryString.set("permitTypeId", permitTypeId);
    } else {
      queryString.delete("permitTypeId");
    }

    setPaginationInfo({ ...paginationInfo, pageIndex: 0 });

    history.push({
      search: queryString.toString(),
    });
  };

  useEffect(() => {
    if (currentPermitRegister && paginationInfo.pageSize > 0)
      getPermits({
        variables: {
          permitRegisterId: currentPermitRegister.id,
          skip: paginationInfo.pageIndex * paginationInfo.pageSize,
          take: paginationInfo.pageSize,
          searchString,
          stateKey: stateFilter,
          assignedToId: assignedToFilter,
          createdById: createdByFilter,
          asBuiltRequired: asBuiltRequiredFilter,
          permitTypeId: permitTypeFilter || null,
        },
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paginationInfo.pageSize,
    paginationInfo.pageIndex,
    currentPermitRegister,
    searchString,
    stateFilter,
    assignedToFilter,
    createdByFilter,
    asBuiltRequiredFilter,
    permitTypeFilter,
  ]);

  /* const handlePermitListUpdate = () => {
    refetchPermits && refetchPermits();
  }; */

  const showAsBuiltColumn =
    currentPermitRegister?.permitTypes?.some((pt) => pt.template?.features?.asBuilt?.enabled) ?? false;

  return (
    <PermitsList
      showPermitBoundariesOnMap={
        currentOrganization?.esriConfig?.integrationTypeId != null &&
        currentOrganization.esriConfig.integrationTypeId !== EsriIntegrationTypes.NONE.id
      }
      showAsBuiltColumn={showAsBuiltColumn}
      newPermitButtonVisible={authorizedToAddPermit}
      requestPermitButtonVisible={authorizedToRequestPermit}
      permitTypes={currentPermitRegister?.permitTypes}
      permits={permits?.permitToDigsByPermitRegisterId?.items && permits.permitToDigsByPermitRegisterId.items}
      currentProject={currentProject}
      pageCount={
        permits && Math.ceil((permits?.permitToDigsByPermitRegisterId?.totalCount ?? 0) / paginationInfo.pageSize)
      }
      stateFilter={stateFilter}
      assignedToFilter={assignedToFilter}
      createdByFilter={createdByFilter}
      searchString={searchString}
      asBuiltRequiredFilter={asBuiltRequiredFilter}
      permitTypeFilter={permitTypeFilter}
      onPageInfoChange={handlePageInfoChange}
      onSearchPermits={handleSearchPermits}
      onStateFilter={handleStateFilter}
      onAssignedToFilter={handleAssignedToFilter}
      onCreatedByFilter={handleCreatedByFilter}
      onAsBuiltRequiredFilterChange={handleAsBuiltRequiredFilterChange}
      onPermitTypeFilter={handlePermitTypeFilter}
      getPermitsStatus={getPermitsStatus}
      permitCreators={filterOptionsData?.permitRegister?.permitCreators ?? []}
      assignedOrgUsers={filterOptionsData?.permitRegister?.assignedOrgUsers ?? []}
    ></PermitsList>
  );
};

export default PermitsListContainer;
